import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, UserCheck, AlertTriangle } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { api, formatApiError } from '@/lib/api';
import { recruitmentClientApi } from '@/lib/recruitmentApi';
import { toast } from 'sonner';

// Founder UAT 2026-06 — hired candidate → collaborator → position occupant.
// Explicit confirmation dialog: shows the identity/placement that will be
// created or linked, preselects the process's formal position when vacant,
// blocks occupied positions server-side (409, no silent displacement).

const NONE = '__none__';

export default function ConvertToCollaboratorDialog({ open, onClose, processId, candidate, onConverted }) {
  const { t, i18n } = useTranslation();
  const isES = i18n.language?.startsWith('es');
  const L = (es, en) => (isES ? es : en);

  const [positions, setPositions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [positionId, setPositionId] = useState(NONE);
  const [departmentId, setDepartmentId] = useState(NONE);
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [employmentType, setEmploymentType] = useState(NONE);
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(null);

  useEffect(() => {
    if (!open) return;
    setConflict(null);
    setBusy(false);
    Promise.all([
      api.get('/client/positions').then((r) => r.data).catch(() => []),
      api.get('/client/departments').then((r) => r.data).catch(() => []),
      recruitmentClientApi.get(processId).catch(() => null),
    ]).then(([pos, deps, proc]) => {
      const list = Array.isArray(pos) ? pos : [];
      setPositions(list);
      setDepartments(Array.isArray(deps) ? deps : []);
      const fp = proc?.formal_position_id;
      const fpRow = fp && list.find((p) => p.id === fp);
      const vacantFp = fpRow && (fpRow.occupied || 0) < (fpRow.headcount || 1);
      setPositionId(vacantFp ? fp : NONE);
      setDepartmentId(vacantFp && fpRow.department_id ? fpRow.department_id : NONE);
    });
  }, [open, processId]);

  const selPos = useMemo(() => positions.find((p) => p.id === positionId), [positions, positionId]);

  useEffect(() => {
    if (selPos?.department_id) setDepartmentId(selPos.department_id);
  }, [selPos]);

  const posLabel = (p) => {
    const cap = p.headcount || 1;
    const occ = p.occupied || 0;
    const state = occ >= cap
      ? L('Ocupado', 'Occupied')
      : L(`Vacante (${cap - occ})`, `Vacant (${cap - occ})`);
    return `${p.title} — ${state}`;
  };

  const confirm = async () => {
    setBusy(true);
    setConflict(null);
    try {
      const body = {
        assignment_id: candidate?.assignment_id || null,
        pool_candidate_id: candidate?.pool_candidate_id || null,
        position_id: positionId === NONE ? null : positionId,
        department_id: departmentId === NONE ? null : departmentId,
        start_date: startDate || null,
        employment_type: employmentType === NONE ? null : employmentType,
        confirm_selection: true,
      };
      const { data } = await api.post(`/admin/recruitment-processes/${processId}/convert-to-collaborator`, body);
      const isAdminPortal = window.location.pathname.startsWith('/admin');
      if (data.already) {
        toast.success(L('Este candidato ya estaba activado como colaborador.', 'This candidate was already activated as an employee.'));
      } else {
        toast.success(
          L(`${data.name} ha sido incorporado/a correctamente como colaborador(a).`,
            `${data.name} has been successfully onboarded as an employee.`),
          {
            duration: 9000,
            description: data.future_start
              ? L(`Contratación confirmada. Inicio: ${data.start_date}. Aparecerá en Colaboradores a partir de esa fecha.`,
                  `Hire confirmed. Start date: ${data.start_date}. Will appear in Collaborators from that date.`)
              : (data.unlinked
                  ? L('Colaborador activo sin puesto formal vinculado.', 'Active collaborator without a formal linked position.')
                  : L(`Ahora ocupa el puesto: ${data.position_title}`, `Now occupies the position: ${data.position_title}`)),
            action: (!isAdminPortal && !data.future_start) ? {
              label: L('Ver ficha del colaborador', 'View employee profile'),
              onClick: () => { window.location.href = `/client/employees?q=${encodeURIComponent(data.name || '')}`; },
            } : undefined,
          },
        );
      }
      onConverted?.(data);
      onClose?.();
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 409 && det?.code === 'position_occupied') {
        setConflict(det);
      } else if (det === 'candidate_not_in_hired_state') {
        toast.error(L('El candidato aún no está en estado de contratación/preselección.',
          'The candidate is not yet in a hired/selected state.'));
      } else {
        toast.error(formatApiError(e));
      }
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="sm:max-w-lg" data-testid="convert-collaborator-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-emerald-600" />
            {L('Confirmar y activar colaborador', 'Confirm & activate employee')}
          </DialogTitle>
          <DialogDescription>
            {L('El candidato se convertirá en colaborador activo de tu organización, conservando su identidad e historial. No se crean duplicados.',
              'The candidate becomes an active collaborator of your organization, preserving identity and history. No duplicates are created.')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg border p-3 space-y-1 text-sm bg-muted/30" data-testid="convert-summary">
            <p><span className="text-muted-foreground">{L('Nombre', 'Name')}:</span> <b>{candidate?.candidate_name || '—'}</b></p>
            <p><span className="text-muted-foreground">{L('Correo', 'Email')}:</span> {candidate?.candidate_email || '—'}</p>
            <p>
              <span className="text-muted-foreground">{L('Estado laboral', 'Employment status')}:</span>{' '}
              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">
                {L('Colaborador activo', 'Active collaborator')}
              </Badge>
            </p>
          </div>

          <div className="space-y-2">
            <Label>{L('Puesto formal (opcional)', 'Formal position (optional)')}</Label>
            <Select value={positionId} onValueChange={setPositionId}>
              <SelectTrigger data-testid="convert-position-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>{L('Sin puesto formal', 'No formal position')}</SelectItem>
                {positions.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`convert-position-${p.id}`}>
                    {posLabel(p)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{L('Departamento', 'Department')}</Label>
            <Select value={departmentId} onValueChange={setDepartmentId}>
              <SelectTrigger data-testid="convert-department-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>{L('Sin departamento', 'No department')}</SelectItem>
                {departments.map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>{L('Fecha de inicio', 'Start date')}</Label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                     className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                     data-testid="convert-start-date" />
            </div>
            <div className="space-y-2">
              <Label>{L('Tipo de empleo (opcional)', 'Employment type (optional)')}</Label>
              <Select value={employmentType} onValueChange={setEmploymentType}>
                <SelectTrigger data-testid="convert-employment-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{L('Sin especificar', 'Not specified')}</SelectItem>
                  <SelectItem value="permanent">{L('Permanente', 'Permanent')}</SelectItem>
                  <SelectItem value="temporary">{L('Temporal', 'Temporary')}</SelectItem>
                  <SelectItem value="contractor">{L('Por contrato', 'Contractor')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {startDate > new Date().toISOString().slice(0, 10) && (
            <p className="text-xs text-sky-800 bg-sky-50 border border-sky-200 rounded-md p-2.5" data-testid="convert-future-note">
              {L('La contratación quedará confirmada hoy, pero el colaborador se activará y aparecerá en Colaboradores en la fecha de inicio indicada.',
                 'The hire is confirmed today, but the employee activates and appears in Collaborators on the chosen start date.')}
            </p>
          )}

          {positionId === NONE && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2.5" data-testid="convert-unlinked-warning">
              {L('Se registrará como "Colaborador activo sin puesto formal vinculado". Podrás vincularlo a un puesto más adelante.',
                'Will be registered as "Active collaborator without a formal linked position". You can link a position later.')}
            </p>
          )}

          {conflict && (
            <div className="text-xs text-destructive bg-destructive/5 border border-destructive/30 rounded-md p-2.5 flex items-start gap-2" data-testid="convert-conflict">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                {L(`El puesto "${conflict.position_title}" ya está ocupado (${(conflict.occupants || []).join(', ')}). Selecciona un puesto vacante, crea uno nuevo o resuelve la ocupación existente mediante el flujo organizacional. No se reemplaza al ocupante actual.`,
                  `Position "${conflict.position_title}" is already occupied (${(conflict.occupants || []).join(', ')}). Choose a vacant position, create a new one, or resolve the existing occupancy through the organizational workflow. The current occupant is never displaced.`)}
              </span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="convert-cancel">
            {t('common.cancel')}
          </Button>
          <Button onClick={confirm} disabled={busy} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" data-testid="convert-confirm">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {L('Confirmar y activar colaborador', 'Confirm & activate employee')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
